import { RandomSourceWithSeed } from "common/utils/random";
import * as THREE from "three";
import { safeShaderReplace } from "./materials/shaderUtils";

const INSTANCE_COUNT = 2000;

const randomSource = new RandomSourceWithSeed(1);

const tmpPosition = new THREE.Vector3();
const tmpRotation = new THREE.Euler();
const tmpQuaternion = new THREE.Quaternion();
const tmpScale = new THREE.Vector3(1, 1, 1);

function randomizeMatrix(matrix) {
  const areaW = 300;

  tmpPosition.x = randomSource.next() * areaW - areaW * 0.5;
  tmpPosition.y = randomSource.next() * areaW - areaW * 0.5;
  tmpPosition.z = randomSource.next() * areaW - areaW * 0.5;

  tmpRotation.x = randomSource.next() * 2 * Math.PI;
  tmpRotation.y = randomSource.next() * 2 * Math.PI;
  tmpRotation.z = randomSource.next() * 2 * Math.PI;
  tmpQuaternion.setFromEuler(tmpRotation);

  matrix.compose(tmpPosition, tmpQuaternion, tmpScale);
}

function createInstances(geometry, material) {
  const matrix = new THREE.Matrix4();
  const mesh = new THREE.InstancedMesh(geometry, material, INSTANCE_COUNT);

  for (let i = 0; i < INSTANCE_COUNT; i++) {
    randomizeMatrix(matrix);
    mesh.setMatrixAt(i, matrix);
  }
  return mesh;
}

/**
 * Note that the results from this function can be highly variable. Designed to mostly stress shader ALU and fillrate.
 * Calibrated for measuring Apple M-series GPUs, since we don't get accurate renderer info on Safari using other means.
 * @returns an FPS value that's roughly comparable to results from detect-gpu.
 */
export function benchmarkGpu(renderer: THREE.WebGLRenderer) {
  const renderTarget = new THREE.WebGLRenderTarget(512, 512);
  renderer.setRenderTarget(renderTarget);

  const camera = new THREE.PerspectiveCamera(70, 1, 1, 1000);
  camera.position.z = 400;

  const scene = new THREE.Scene();

  const material = new THREE.MeshPhongMaterial({ transparent: true, opacity: 0.5 });
  material.onBeforeCompile = function (shader) {
    // Add some extra computation to the fragment shader to make it run a bit slower.
    shader.fragmentShader = safeShaderReplace(
      shader.fragmentShader,
      "#include <fog_fragment>",
      `for (int ii = 0; ii < 30; ++ii) {
        gl_FragColor = fract(gl_FragColor * 1.64 + 1.24);
      }`,
      "benchmarkGpu (fog_fragment)",
    );
  };

  const geometry = new THREE.BoxGeometry(50, 50, 50, 5, 5, 5);
  const mesh = createInstances(geometry, material);
  scene.add(mesh);

  // Create directional light and add to scene.
  const directionalLight = new THREE.DirectionalLight(0xffffff);
  directionalLight.position.set(1, 1, 1);
  scene.add(directionalLight);

  // Render one frame to load shaders etc.
  renderer.render(scene, camera);
  renderer.getContext().finish();

  const startTime = performance.now();
  // Render another frame to actually measure performance.
  renderer.render(scene, camera);

  const readWidth = 10;
  const buf = new Uint8Array(readWidth * readWidth * 4);
  renderer.readRenderTargetPixels(renderTarget, 0, 0, readWidth, readWidth, buf);
  const endTime = performance.now();

  const fps = 1 / ((endTime - startTime) * 0.001);

  renderer.setRenderTarget(null);
  geometry.dispose();
  return fps;
}
